import { fail, pass } from "@/core/check";
import type { Lesson } from "@/core/types";
import { readThemeColors } from "@/office/powerpoint/design";
import { nichtPruefbar } from "../schnittstelle";

/**
 * Die Akzentfarben des Office-Standarddesigns. Stimmen alle sechs überein, hat
 * niemand eigene Hausfarben hinterlegt.
 */
const OFFICE_STANDARD = ["4472C4", "ED7D31", "A5A5A5", "FFC000", "5B9BD5", "70AD47"];

export const lektionCorporateDesign: Lesson = {
  id: "corporate-design",
  title: "Hausfarben und Hausschriften",
  summary: "Das Corporate Design einmal ins Design legen statt in jede Folie.",
  durationMinutes: 10,
  learningGoals: [
    "Eigene Farbpaletten im Design hinterlegen",
    "Hausschriften als Designschriftarten festlegen",
    "Das Ergebnis als Design und als Vorlage weitergeben",
  ],
  steps: [
    {
      id: "farben-anpassen",
      title: "Eigene Farben hinterlegen",
      explanation: [
        "Fast jede Organisation hat festgelegte Farben. Der verbreitete Umgang damit " +
          "ist, den Hex-Wert irgendwo notiert zu haben und ihn bei Bedarf über „Weitere " +
          "Farben“ einzutippen. Das führt zuverlässig zu leicht abweichenden Farbtönen " +
          "über die Präsentation hinweg und überlebt keinen Designwechsel.",
        "Der richtige Ort ist das Design: „Entwurf“ → „Varianten“ → „Farben“ → „Farben " +
          "anpassen“. Der Dialog zeigt zwölf Felder – vier für Text und Hintergrund, " +
          "sechs Akzentfarben, zwei für Hyperlinks. Die Hausfarben kommen auf die " +
          "Akzentplätze, die wichtigste auf „Akzent 1“.",
        "Nach dem Speichern unter einem Namen stehen sie in der oberen Hälfte jeder " +
          "Farbpalette. Ab da wählt man nicht mehr einen Farbwert aus, sondern eine " +
          "Rolle – mit allen Vorteilen aus dem Modul „Folienmaster und Design“.",
        "Ein Hinweis zu Text und Hintergrund: Die vier Felder heißen „Dunkel 1“, " +
          "„Hell 1“, „Dunkel 2“, „Hell 2“ und sind paarweise gedacht. PowerPoint nutzt " +
          "sie automatisch so, dass Text auf Hintergrund lesbar bleibt. Wer dort ein " +
          "helles Grau als „Dunkel 1“ einträgt, hebelt genau diesen Mechanismus aus – " +
          "und bekommt Kontrastprobleme auf allen Folien.",
      ],
      keyPoints: [
        "Entwurf → Varianten → Farben → Farben anpassen.",
        "Hausfarben auf die Akzentplätze, die wichtigste auf „Akzent 1“.",
        "„Dunkel 1“ und „Hell 1“ sind ein Paar – dort gehört kein Mittelton hinein.",
      ],
      task: {
        instruction:
          "Hinterlege eigene Designfarben, statt mit dem Office-Standarddesign zu " +
          "arbeiten – mindestens eine Akzentfarbe soll von der Voreinstellung abweichen.",
        hint: "Entwurf → Varianten → Farben → „Farben anpassen“, dann unter einem Namen speichern.",
        check: async () => {
          const farben = await readThemeColors();

          if (farben === null) {
            return nichtPruefbar(
              "Die Farben des Designs",
              "1.10",
              "Sieh unter „Entwurf“ → „Varianten“ → „Farben“ nach, ob dort oben eine benutzerdefinierte Palette markiert ist.",
            );
          }

          const akzente = [
            farben.Accent1,
            farben.Accent2,
            farben.Accent3,
            farben.Accent4,
            farben.Accent5,
            farben.Accent6,
          ].map((wert) => wert.trim().replace("#", "").toUpperCase());

          const abweichend = akzente.filter(
            (wert, index) => wert.length === 6 && wert !== OFFICE_STANDARD[index],
          );

          if (abweichend.length === 0) {
            return fail(
              "Die sechs Akzentfarben entsprechen genau dem Office-Standarddesign.",
              "Entwurf → Varianten → Farben → „Farben anpassen“: Trag deine Hausfarben auf die Akzentplätze ein und speichere die Palette unter einem Namen.",
            );
          }

          return pass(
            `${abweichend.length} von 6 Akzentfarben weichen vom Office-Standard ab (zum Beispiel #${abweichend[0]}) – ` +
              "die Präsentation arbeitet mit einer eigenen Palette.",
          );
        },
      },
    },
    {
      id: "schriften",
      title: "Hausschriften festlegen",
      explanation: [
        "Neben den Farben lassen sich auch die beiden Designschriftarten festlegen: " +
          "„Entwurf“ → „Varianten“ → „Schriftarten“ → „Schriftarten anpassen“. Der " +
          "Dialog fragt nach genau zwei Schriften – eine für Überschriften, eine für den " +
          "Textkörper.",
        "Zwei ist dabei keine Einschränkung, sondern eine gute Gestaltungsregel. Mehr " +
          "als zwei Schriftarten in einer Präsentation wirken unruhig, und die " +
          "Unterscheidung zwischen Überschrift und Text lässt sich auch über Größe und " +
          "Stärke herstellen.",
        "Bei der Auswahl ist die Verfügbarkeit wichtiger als der Geschmack. Eine " +
          "Hausschrift, die nur auf den Rechnern der Marketingabteilung installiert ist, " +
          "wird auf allen anderen Geräten ersetzt – und dann verschiebt sich das Layout. " +
          "Wenn die Hausschrift keine Systemschrift ist, gehören in die Vorlage entweder " +
          "eingebettete Schriftarten oder eine verbreitete Ersatzschrift.",
        "Für die Weitergabe gilt: Das fertige Paket aus Farben, Schriften und Effekten " +
          "wird über „Entwurf“ → Aufklappmenü → „Aktuelles Design speichern“ zu einer " +
          "Datei, die sich per Doppelklick installieren und dann auf jede Präsentation " +
          "anwenden lässt.",
      ],
      keyPoints: [
        "Entwurf → Varianten → Schriftarten → Schriftarten anpassen.",
        "Genau zwei Schriften: Überschrift und Textkörper.",
        "Nicht installierte Hausschriften einbetten oder eine Ersatzschrift festlegen.",
      ],
    },
    {
      id: "im-team",
      title: "Vorlagen im Team pflegen",
      explanation: [
        "Eine Vorlage auf dem eigenen Rechner hilft nur einem. Für ein Team gibt es " +
          "mehrere Wege mit unterschiedlichem Aufwand.",
        "Am einfachsten ist ein gemeinsamer Netzwerk- oder SharePoint-Ordner, den alle " +
          "kennen. Unter „Datei“ → „Optionen“ → „Erweitert“ → „Dateispeicherorte“ lässt " +
          "sich zusätzlich ein Pfad für Arbeitsgruppenvorlagen eintragen – Vorlagen von " +
          "dort erscheinen bei allen unter „Datei“ → „Neu“.",
        "In Organisationen mit Microsoft 365 übernimmt das die " +
          "Organisationsvorlagen-Funktion, die eine Dokumentbibliothek zentral " +
          "bereitstellt. Der Vorteil ist die Aktualisierung: Wird die Vorlage dort " +
          "ausgetauscht, arbeiten alle ab dem nächsten Mal mit der neuen Fassung.",
        "Der wichtigste Punkt beim Pflegen: Bereits erstellte Präsentationen ziehen " +
          "Änderungen an der Vorlage nicht nach. Wer die Vorlage überarbeitet, sollte das " +
          "ankündigen. Für laufende Präsentationen ist ein separat gespeichertes Design " +
          "(.thmx) der bessere Weg – das lässt sich nachträglich anwenden.",
      ],
      keyPoints: [
        "Datei → Optionen → Erweitert → Dateispeicherorte für Arbeitsgruppenvorlagen.",
        "Bestehende Präsentationen übernehmen Vorlagenänderungen nicht rückwirkend.",
        "Für laufende Präsentationen ein Design (.thmx) nachliefern.",
      ],
    },
  ],
};
